import React, { useState, useEffect, useMemo, Fragment } from "react";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Spinner,
  Badge,
  Table,
  OverlayTrigger,
  Tooltip,
  Alert,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import {
  getActiveReceiptsForAccount,
  requestReceiptReversal,
} from "../../afinanceapi";
import "./acc-list.css";

/**
 * ReceiptReversalModal — single entry point for the receipt-reversal flow.
 *
 * Lists every active receipt on the account, scoped to the user's practices,
 * with the full allocation breakdown per receipt. The Reverse button is
 * disabled for ERA-sourced receipts (with a tooltip pointing to the ERA
 * recon view) and for receipts whose unallocated portion has been drawn
 * down by a later invoice. Confirmation happens in-place — the card body
 * swaps into a reason textarea + confirm/cancel; no second modal.
 *
 * Visual conventions: see /CLAUDE.md "frontend design system". This file
 * uses the .acc-list / .acc-card pattern + the ZAR currency formatter +
 * the modal-header tint defined there.
 */
const ZAR = new Intl.NumberFormat("en-ZA", {
  style: "currency",
  currency: "ZAR",
});

function formatDate(d) {
  if (!d) return "—";
  try {
    const parsed = new Date(d);
    if (Number.isNaN(parsed.getTime())) return String(d);
    return parsed.toLocaleDateString("en-GB");
  } catch {
    return String(d);
  }
}

function ReceiptReversalModal({ show, onHide, account }) {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  // Server-side sort — matches the AccountList pattern (single key +
  // direction, arrow only on the active column). Backend whitelist is in
  // receiptRoutes.js GET /accounts/:accountId/receipts/active.
  const [sortConfig, setSortConfig] = useState({
    key: "receiptDate",
    direction: "desc",
  });
  const [confirmingId, setConfirmingId] = useState(null);
  const [confirmReason, setConfirmReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successToast, setSuccessToast] = useState(null);
  // Tracks Finance-submitted approval requests that haven't resolved yet,
  // keyed by receiptId. Cleared when the modal is re-opened (reload runs
  // from scratch — backend doesn't surface "has pending approval task" on
  // the list endpoint in v1, so the banner is intentionally session-scoped).
  const [pendingApprovals, setPendingApprovals] = useState({});

  // Role is derived from the JWT — single source of truth, matches every
  // other role check in the codebase (role/roles/permissions union).
  const role = useMemo(() => {
    const t =
      sessionStorage.getItem("userToken") || sessionStorage.getItem("user");
    if (!t) return null;
    try {
      const claims = jwtDecode(t);
      const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
      const roles = new Set([
        ...asArray(claims.role),
        ...asArray(claims.roles),
        ...asArray(claims.permissions),
      ]);
      if (roles.has("SuperAdmin")) return "SuperAdmin";
      if (roles.has("Finance")) return "Finance";
      return "Other";
    } catch {
      return null;
    }
  }, []);
  const isSuperAdmin = role === "SuperAdmin";

  async function load(sort = sortConfig) {
    if (!account?._id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getActiveReceiptsForAccount(account._id, sort);
      setReceipts(data.receipts || []);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load receipts."
      );
    } finally {
      setLoading(false);
    }
  }

  // Server-side sort toggle — flips direction when re-clicking the active
  // column, otherwise switches column with the prior direction reset to
  // asc. Reloads in the same call so we don't depend on a sortConfig
  // effect (which would race the open-modal reset effect).
  function toggleSort(key) {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    const next = { key, direction };
    setSortConfig(next);
    setConfirmingId(null);
    setActionError(null);
    load(next);
  }

  function renderSortIcon(key) {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  }

  useEffect(() => {
    if (show) {
      setConfirmingId(null);
      setConfirmReason("");
      setActionError(null);
      setSuccessToast(null);
      setSearch("");
      setPendingApprovals({});
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, account?._id]);

  const filteredReceipts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return receipts;
    return receipts.filter((r) => {
      if (String(r.receiptNumber || "").toLowerCase().includes(q)) return true;
      if (String(r.receiptDate || "").toLowerCase().includes(q)) return true;
      return (r.receiptInvoices || []).some((row) =>
        String(row.invoiceNumberResolved || row.invoiceNumber || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [receipts, search]);

  // One row per (receipt × allocation) for the desktop table. Receipts with
  // no allocations still get one row (allocation=null) so they stay visible.
  const flatRows = useMemo(() => {
    const out = [];
    filteredReceipts.forEach((receipt) => {
      const allocations = receipt.receiptInvoices || [];
      const list = allocations.length > 0 ? allocations : [null];
      list.forEach((allocation, idx) => {
        out.push({
          key: `${receipt._id}-${idx}`,
          receipt,
          allocation,
          isLastOfReceipt: idx === list.length - 1,
        });
      });
    });
    return out;
  }, [filteredReceipts]);

  function isDrawnDown(receipt) {
    return (receipt.receiptInvoices || []).some(
      (row) =>
        (row.invoiceType === "Unallocated" ||
          row.invoiceNumber === "Unallocated Funds") &&
        Number(row.usedAmount || 0) > 0
    );
  }

  function disabledReason(receipt) {
    if (receipt.eraSourced) {
      return "This receipt was created from an ERA reconciliation. Reverse the ERA recon in the ERA Reports view, not here.";
    }
    if (isDrawnDown(receipt)) {
      const total = (receipt.receiptInvoices || [])
        .filter(
          (row) =>
            row.invoiceType === "Unallocated" ||
            row.invoiceNumber === "Unallocated Funds"
        )
        .reduce((s, row) => s + Number(row.usedAmount || 0), 0);
      return `${ZAR.format(total)} of unallocated funds have already been used on a later invoice. Reverse that payment first.`;
    }
    return null;
  }

  async function handleReverse(receipt) {
    const reason = confirmReason.trim();
    if (!reason) {
      setActionError("Reason is required.");
      return;
    }
    setSubmitting(true);
    setActionError(null);
    try {
      const result = await requestReceiptReversal(receipt._id, { reason });
      if (result.status === "done") {
        const n = result.summary?.affectedInvoices?.length || 0;
        setSuccessToast(
          `Receipt ${receipt.receiptNumber} reversed. ${n} invoice(s) reset.`
        );
        setConfirmingId(null);
        setConfirmReason("");
        await load();
      } else if (result.status === "pending_approval") {
        const n = result.taskIds?.length || 0;
        setSuccessToast(`Approval requested — sent to ${n} SuperAdmin(s).`);
        setPendingApprovals((prev) => ({
          ...prev,
          [receipt._id]: {
            approvalGroupId: result.approvalGroupId,
            taskIds: result.taskIds || [],
            requestedAt: new Date().toISOString(),
          },
        }));
        setConfirmingId(null);
        setConfirmReason("");
        // Don't reload — the receipt is still active until a SA approves;
        // the banner stays visible until the user re-opens the modal.
      }
    } catch (err) {
      const code = err?.response?.data?.error;
      const details = err?.response?.data?.details;
      if (code === "era_sourced_receipt") {
        setActionError(
          "This receipt was created from an ERA reconciliation. Reverse the ERA recon in the ERA Reports view, not here."
        );
      } else if (code === "drawn_down") {
        setActionError(
          `${ZAR.format(Number(details?.usedAmount || 0))} of this receipt's unallocated funds have already been used downstream. Reverse that payment first.`
        );
      } else if (code === "already_reversed") {
        setActionError(
          "This receipt has already been reversed by another user."
        );
      } else if (code === "no_super_admins_in_practice") {
        setActionError(
          "This practice has no SuperAdmins configured. Contact your administrator."
        );
      } else {
        setActionError(code || err?.message || "Reversal failed.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const headerName = account
    ? [account.accountFirstName, account.accountLastName]
        .filter(Boolean)
        .join(" ") ||
      account.accountFullNames ||
      account.accountNumber
    : "";

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Receipt Reversal{headerName ? ` — ${headerName}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {successToast && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccessToast(null)}
            className="mb-3"
          >
            {successToast}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <InputGroup className="mb-3 align-items-stretch">
          <InputGroup.Text>
            <i className="fa-solid fa-magnifying-glass fa-lg" />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search receipt number, invoice number, or date"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 mb-0 text-muted">Loading receipts…</p>
          </div>
        ) : filteredReceipts.length === 0 ? (
          <p className="text-center text-muted mb-0 py-4">
            {receipts.length === 0
              ? "No active receipts on this account."
              : "No receipts match your search."}
          </p>
        ) : (
          <>
            {/* Desktop: flat table — one row per (receipt × allocation). Click
                any row to open the in-line reversal form for that receipt. */}
            <div
              className="d-none d-lg-block acc-list"
              style={{ padding: 0, boxShadow: "none", border: "none" }}
            >
              <Table bordered hover className="mb-0">
                <thead className="text-center">
                  <tr>
                    <th
                      className="text-primary fw-semibold"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("receiptDate")}
                    >
                      <div className="d-flex justify-content-center align-items-center">
                        <span>Receipt Date</span>
                        {renderSortIcon("receiptDate")}
                      </div>
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("receiptNumber")}
                    >
                      <div className="d-flex justify-content-center align-items-center">
                        <span>Receipt Number</span>
                        {renderSortIcon("receiptNumber")}
                      </div>
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("invoiceDate")}
                    >
                      <div className="d-flex justify-content-center align-items-center">
                        <span>Invoice Date</span>
                        {renderSortIcon("invoiceDate")}
                      </div>
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("invoiceNumber")}
                    >
                      <div className="d-flex justify-content-center align-items-center">
                        <span>Invoice Number</span>
                        {renderSortIcon("invoiceNumber")}
                      </div>
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("receiptMethod")}
                    >
                      <div className="d-flex justify-content-center align-items-center">
                        <span>Method</span>
                        {renderSortIcon("receiptMethod")}
                      </div>
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("paymentSource")}
                    >
                      <div className="d-flex justify-content-center align-items-center">
                        <span>Source</span>
                        {renderSortIcon("paymentSource")}
                      </div>
                    </th>
                    <th
                      className="text-primary fw-semibold acc-list-num"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort("paymentAmount")}
                    >
                      <div className="d-flex justify-content-end align-items-center">
                        <span>Amount</span>
                        {renderSortIcon("paymentAmount")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {flatRows.map((row) => {
                    const { receipt, allocation, isLastOfReceipt } = row;
                    const pending = pendingApprovals[receipt._id];
                    const blockReason = disabledReason(receipt);
                    const isConfirming = confirmingId === receipt._id;
                    const clickable = !blockReason && !pending;
                    const isUnalloc =
                      allocation &&
                      (allocation.invoiceType === "Unallocated" ||
                        allocation.invoiceNumber === "Unallocated Funds");
                    const buttonVariant = isSuperAdmin ? "danger" : "primary";
                    const groupEnd =
                      isLastOfReceipt && !isConfirming && !pending
                        ? "acc-statement-group-end"
                        : "";
                    const activeClass = isConfirming ? "table-active" : "";

                    return (
                      <Fragment key={row.key}>
                        <tr
                          className={[groupEnd, activeClass]
                            .filter(Boolean)
                            .join(" ")}
                          style={{
                            cursor: clickable ? "pointer" : "not-allowed",
                            opacity: blockReason || pending ? 0.55 : 1,
                            background:
                              blockReason || pending
                                ? "rgba(0, 0, 0, 0.03)"
                                : undefined,
                            color:
                              blockReason || pending ? "#6c757d" : undefined,
                            userSelect:
                              blockReason || pending ? "none" : undefined,
                          }}
                          onClick={() => {
                            if (!clickable) return;
                            if (isConfirming) {
                              setConfirmingId(null);
                              setActionError(null);
                            } else {
                              setConfirmingId(receipt._id);
                              setConfirmReason("");
                              setActionError(null);
                            }
                          }}
                          title={
                            blockReason ||
                            (pending ? "Awaiting SuperAdmin approval" : undefined)
                          }
                        >
                          <td>{formatDate(receipt.receiptDate)}</td>
                          <td>
                            {receipt.receiptNumber}
                            {receipt.eraSourced && (
                              <Badge
                                bg="warning"
                                text="dark"
                                className="ms-2"
                                style={{ fontSize: "0.65rem" }}
                              >
                                ERA
                              </Badge>
                            )}
                            {pending && (
                              <Badge
                                bg="info"
                                className="ms-2"
                                style={{ fontSize: "0.65rem" }}
                              >
                                Pending
                              </Badge>
                            )}
                          </td>
                          <td>
                            {allocation
                              ? formatDate(
                                  allocation.invoiceDateResolved ||
                                    allocation.invoiceDate
                                )
                              : "—"}
                          </td>
                          <td>
                            {!allocation ? (
                              "—"
                            ) : isUnalloc ? (
                              <em>Unallocated funds</em>
                            ) : (
                              allocation.invoiceNumberResolved ||
                              allocation.invoiceNumber ||
                              "—"
                            )}
                            {isUnalloc &&
                              Number(allocation?.usedAmount || 0) > 0 && (
                                <span className="text-danger small ms-2">
                                  ({ZAR.format(Number(allocation.usedAmount))}{" "}
                                  used)
                                </span>
                              )}
                          </td>
                          <td>
                            {receipt.receiptMethod ||
                              receipt.receiptPaymentMethod ||
                              "—"}
                          </td>
                          <td>
                            {allocation?.paymentSource === "medicalAid"
                              ? "Medical Aid"
                              : allocation?.paymentSource === "patient"
                                ? "Patient"
                                : allocation?.paymentSource || "—"}
                          </td>
                          <td className="acc-list-num">
                            {allocation
                              ? ZAR.format(Number(allocation.paymentAmount || 0))
                              : ZAR.format(
                                  Number(receipt.receiptTotalAmount || 0)
                                )}
                          </td>
                        </tr>
                        {isLastOfReceipt && pending && (
                          <tr className="acc-statement-group-end">
                            <td colSpan={7} className="py-2">
                              <div className="posted-invoice-info">
                                <div className="small">
                                  Approval requested — sent to{" "}
                                  {pending.taskIds.length} SuperAdmin
                                  {pending.taskIds.length === 1 ? "" : "s"}.
                                  {pending.taskIds[0] && (
                                    <>
                                      {" "}
                                      <a
                                        href={`/?taskId=${pending.taskIds[0]}`}
                                        className="text-primary fw-semibold"
                                      >
                                        View task
                                      </a>
                                      .
                                    </>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        {isLastOfReceipt && isConfirming && (
                          <tr className="acc-statement-group-end">
                            <td
                              colSpan={7}
                              style={{
                                background: "rgba(207, 226, 255, 0.18)",
                              }}
                            >
                              <p className="mb-2">
                                You're about to reverse receipt{" "}
                                <strong className="text-primary">
                                  {receipt.receiptNumber}
                                </strong>{" "}
                                for{" "}
                                <strong>
                                  {ZAR.format(
                                    Number(receipt.receiptTotalAmount || 0)
                                  )}
                                </strong>
                                .
                                {(receipt.receiptInvoices || []).length > 1 && (
                                  <span className="text-muted small ms-1">
                                    This resets{" "}
                                    {receipt.receiptInvoices.length} allocations.
                                  </span>
                                )}
                              </p>
                              <Form.Group className="mb-2">
                                <Form.Label>
                                  Reason{" "}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  value={confirmReason}
                                  onChange={(e) =>
                                    setConfirmReason(e.target.value)
                                  }
                                  placeholder="Why is this being reversed?"
                                />
                              </Form.Group>
                              {actionError && (
                                <Alert
                                  variant="danger"
                                  className="py-2 mb-2 small"
                                >
                                  {actionError}
                                </Alert>
                              )}
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  variant={buttonVariant}
                                  size="sm"
                                  onClick={() => handleReverse(receipt)}
                                  disabled={
                                    submitting || !confirmReason.trim()
                                  }
                                >
                                  {submitting ? (
                                    <Spinner size="sm" animation="border" />
                                  ) : (
                                    <>
                                      <i className="fa-solid fa-check me-2" />
                                      Confirm{" "}
                                      {isSuperAdmin ? "reversal" : "request"}
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => {
                                    setConfirmingId(null);
                                    setActionError(null);
                                  }}
                                  disabled={submitting}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* Mobile: card view — one card per receipt with the existing
                allocation breakdown table and in-place confirmation panel. */}
            <div
              className="d-lg-none acc-list"
              style={{ padding: 0, boxShadow: "none", border: "none" }}
            >
              {filteredReceipts.map((receipt) => {
                const isConfirming = confirmingId === receipt._id;
                const pending = pendingApprovals[receipt._id];
                const blockReason = disabledReason(receipt);
                const buttonLabel = isSuperAdmin
                  ? "Reverse receipt"
                  : "Request approval";
                const buttonVariant = isSuperAdmin ? "danger" : "primary";

                return (
                  <div
                    className="acc-card"
                    key={receipt._id}
                    style={pending ? { opacity: 0.75 } : undefined}
                  >
                    <div className="acc-card-top">
                      <span className="acc-card-id">
                        {receipt.receiptNumber}
                        {receipt.eraSourced && (
                          <Badge
                            bg="warning"
                            text="dark"
                            className="ms-2"
                            title={
                              receipt.eraId
                                ? `ERA ${receipt.eraId}`
                                : "ERA-sourced"
                            }
                            style={{ fontSize: "0.65rem" }}
                          >
                            ERA-sourced
                          </Badge>
                        )}
                      </span>
                      <span className="acc-card-amount">
                        {ZAR.format(Number(receipt.receiptTotalAmount || 0))}
                      </span>
                      {pending ? (
                        <Badge
                          bg="info"
                          className="px-2 py-1 align-self-center"
                          style={{ fontSize: "0.7rem" }}
                        >
                          <i className="fa-solid fa-hourglass-half me-1" />
                          Pending approval
                        </Badge>
                      ) : !isConfirming &&
                        (blockReason ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{blockReason}</Tooltip>}
                          >
                            <span className="d-inline-block align-self-center">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled
                                style={{ pointerEvents: "none" }}
                              >
                                {buttonLabel}
                              </Button>
                            </span>
                          </OverlayTrigger>
                        ) : (
                          <Button
                            variant={buttonVariant}
                            size="sm"
                            className="align-self-center"
                            onClick={() => {
                              setConfirmingId(receipt._id);
                              setConfirmReason("");
                              setActionError(null);
                            }}
                          >
                            <i className="fa-solid fa-rotate-left me-2" />
                            {buttonLabel}
                          </Button>
                        ))}
                    </div>

                    <div className="acc-card-body">
                      <div className="acc-card-field">
                        <span className="acc-card-label">Date</span>
                        <span className="acc-card-value">
                          {formatDate(receipt.receiptDate)}
                        </span>
                      </div>
                      <div className="acc-card-field">
                        <span className="acc-card-label">Method</span>
                        <span className="acc-card-value">
                          {receipt.receiptMethod ||
                            receipt.receiptPaymentMethod ||
                            "—"}
                        </span>
                      </div>
                    </div>

                    {pending && (
                      <div className="posted-invoice-info mt-2">
                        <div className="small">
                          Approval requested — sent to {pending.taskIds.length}{" "}
                          SuperAdmin
                          {pending.taskIds.length === 1 ? "" : "s"}.
                          {pending.taskIds[0] && (
                            <>
                              {" "}
                              <a
                                href={`/?taskId=${pending.taskIds[0]}`}
                                className="text-primary fw-semibold"
                              >
                                View task
                              </a>
                              .
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    <Table
                      bordered
                      hover
                      responsive
                      size="sm"
                      className="mt-2 mb-0"
                    >
                      <thead className="text-center">
                        <tr>
                          <th className="text-primary fw-semibold">Invoice</th>
                          <th className="text-primary fw-semibold">Date</th>
                          <th className="text-primary fw-semibold acc-list-num">
                            Amount
                          </th>
                          <th className="text-primary fw-semibold">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(receipt.receiptInvoices || []).map((row, idx) => {
                          const isUnalloc =
                            row.invoiceType === "Unallocated" ||
                            row.invoiceNumber === "Unallocated Funds";
                          return (
                            <tr key={idx}>
                              <td>
                                {isUnalloc ? (
                                  <em>Unallocated funds</em>
                                ) : (
                                  row.invoiceNumberResolved ||
                                  row.invoiceNumber ||
                                  "—"
                                )}
                                {isUnalloc &&
                                  Number(row.usedAmount || 0) > 0 && (
                                    <span className="text-danger small ms-2">
                                      ({ZAR.format(Number(row.usedAmount))}{" "}
                                      used)
                                    </span>
                                  )}
                              </td>
                              <td>
                                {formatDate(
                                  row.invoiceDateResolved || row.invoiceDate
                                )}
                              </td>
                              <td className="acc-list-num">
                                {ZAR.format(Number(row.paymentAmount || 0))}
                              </td>
                              <td>
                                {row.paymentSource === "medicalAid"
                                  ? "Medical Aid"
                                  : row.paymentSource === "patient"
                                    ? "Patient"
                                    : row.paymentSource || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    {isConfirming && (
                      <div className="border-top pt-3 mt-3">
                        <p className="mb-2">
                          You're about to reverse receipt{" "}
                          <strong className="text-primary">
                            {receipt.receiptNumber}
                          </strong>{" "}
                          for{" "}
                          <strong>
                            {ZAR.format(
                              Number(receipt.receiptTotalAmount || 0)
                            )}
                          </strong>
                          .
                        </p>
                        <Form.Group className="mb-2">
                          <Form.Label>
                            Reason <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={confirmReason}
                            onChange={(e) => setConfirmReason(e.target.value)}
                            placeholder="Why is this being reversed?"
                          />
                        </Form.Group>
                        {actionError && (
                          <Alert variant="danger" className="py-2 mb-2 small">
                            {actionError}
                          </Alert>
                        )}
                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            variant={buttonVariant}
                            size="sm"
                            onClick={() => handleReverse(receipt)}
                            disabled={submitting || !confirmReason.trim()}
                          >
                            {submitting ? (
                              <Spinner size="sm" animation="border" />
                            ) : (
                              <>
                                <i className="fa-solid fa-check me-2" />
                                Confirm {isSuperAdmin ? "reversal" : "request"}
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              setConfirmingId(null);
                              setActionError(null);
                            }}
                            disabled={submitting}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ReceiptReversalModal;
